import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ChevronRight,
  Crown,
  Megaphone,
  MessageSquare,
  Play,
  Radar,
  Sparkles,
  Users,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/design";
import { useMe } from "@/api/me";
import { useAccounts } from "@/api/accounts";
import { useChats } from "@/api/chats";
import { useMailingStatus } from "@/api/mailing";
import { useStatsOverview } from "@/api/stats";
import { useSubscription } from "@/api/billing";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

export function HomePage() {
  const { data: me } = useMe();
  const { data: mailing } = useMailingStatus();
  const { data: stats } = useStatsOverview(7);
  const { data: sub } = useSubscription();
  const { data: chats } = useChats();
  const { data: accounts } = useAccounts();
  const navigate = useNavigate();
  const firstName = me?.first_name ?? "Гость";
  const running = !!mailing?.running;

  // 7-day window from /stats/overview
  const sent7d = stats?.sent ?? 0;
  const failed7d = stats?.failed ?? 0;
  const totalAttempts = sent7d + failed7d;
  const successRate = totalAttempts > 0 ? Math.round((sent7d / totalAttempts) * 100) : 0;
  const sentSpark = (stats?.series ?? []).map((b) => b.sent);
  const deltaPct = Math.round(stats?.delta_sent_pct ?? 0);

  const enabledChats = mailing?.enabled_chats ?? 0;
  const totalChats = chats?.length ?? 0;
  const enabledAccounts = mailing?.enabled_accounts ?? 0;
  const totalAccounts = accounts?.length ?? 0;

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div>
          <div className="text-sm text-text-muted">Привет,</div>
          <div className="text-2xl font-semibold tracking-tight mt-0.5">{firstName} 👋</div>
        </div>
        <button
          onClick={() => {
            haptic.select();
            navigate("/app/billing");
          }}
          className="h-11 px-3 rounded-full glass flex items-center gap-1.5 hover:bg-white/[0.06] transition-colors"
          aria-label="Подписка"
        >
          <Crown className="size-4 text-accent" />
          <span className="text-[12px] font-medium">
            {sub?.is_active ? sub.plan_title : "Подписка"}
          </span>
        </button>
      </div>

      {/* Subscription banner — shown when no active sub OR <= 3 days remaining */}
      {(!sub?.is_active || (sub.days_remaining != null && sub.days_remaining <= 3)) && (
        <SubscriptionBanner sub={sub} onClick={() => navigate("/app/billing")} />
      )}

      {/* Hero card — running status */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/20 via-transparent to-transparent pointer-events-none"
            aria-hidden
          />
          <div className="relative p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium">
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      running ? "bg-success animate-pulse" : "bg-text-dim",
                    )}
                  />
                  <span className={running ? "text-success" : "text-text-muted"}>
                    {running ? "Рассылка идёт" : "Рассылка остановлена"}
                  </span>
                </div>
                <div className="text-[28px] leading-tight font-semibold tracking-tight mt-2">
                  {running ? (
                    <>
                      Автопостинг{" "}
                      <span className="text-gradient-accent">активен</span>
                    </>
                  ) : (
                    <>
                      Запустите{"\n"}
                      <span className="text-gradient-accent">первую рассылку</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                haptic.impact("medium");
                navigate("/app/mailing");
              }}
              className="w-full h-12 rounded-2xl bg-gradient-accent text-white font-semibold flex items-center justify-center gap-2 shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)] active:scale-[0.98] transition-transform"
            >
              <Play className="size-4 fill-white" />
              {running ? "Открыть монитор" : "Запустить"}
            </button>
          </div>
        </Card>
      </motion.div>

      {/* Live overview — premium bento layout */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-2.5"
      >
        <div className="flex items-center justify-between px-1">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium">
            Обзор
          </div>
          <button
            onClick={() => {
              haptic.select();
              navigate("/app/stats");
            }}
            className="text-xs text-text-dim hover:text-text-muted flex items-center gap-0.5 transition-colors"
          >
            7 дней
            <ChevronRight className="size-3" />
          </button>
        </div>

        {/* Wide hero — sent over 7d with neon area chart */}
        <BentoHero
          value={sent7d}
          delta={deltaPct}
          spark={sentSpark}
          onClick={() => navigate("/app/stats")}
        />

        {/* Two side-by-side: ring (success rate) + list (chats/accounts) */}
        <div className="grid grid-cols-2 gap-2.5">
          <BentoRing
            percent={successRate}
            label="Успех"
            sublabel={
              totalAttempts > 0
                ? `${sent7d.toLocaleString("ru")} из ${totalAttempts.toLocaleString("ru")}`
                : "нет данных"
            }
            onClick={() => navigate("/app/stats")}
          />
          <BentoStatList
            rows={[
              {
                icon: <MessageSquare className="size-3" />,
                label: "Чаты",
                value: enabledChats,
                max: Math.max(totalChats, enabledChats, 1),
                onClick: () => navigate("/app/chats"),
              },
              {
                icon: <Users className="size-3" />,
                label: "Акк-ты",
                value: enabledAccounts,
                max: Math.max(totalAccounts, enabledAccounts, 1),
                onClick: () => navigate("/app/accounts"),
              },
            ]}
          />
        </div>
      </motion.div>

      {/* Quick actions list */}
      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 pb-1">
          Быстрые действия
        </div>

        <ActionRow
          icon={<MessageSquare className="size-4" />}
          title="Добавить чаты"
          subtitle="Импортируйте список ссылок"
          onClick={() => navigate("/app/chats?import=1")}
        />
        <ActionRow
          icon={<Users className="size-4" />}
          title="Подключить аккаунт"
          subtitle="Через номер телефона"
          onClick={() => navigate("/app/accounts")}
        />
        <ActionRow
          icon={<Megaphone className="size-4" />}
          title="Создать шаблон"
          subtitle="С премиум-эмодзи и медиа"
          onClick={() => navigate("/app/templates")}
        />
        <ActionRow
          icon={<Radar className="size-4" />}
          title="Парсинг + ЛС"
          subtitle="Собрать юзеров и написать в личку"
          onClick={() => navigate("/app/outreach")}
        />
        <ActionRow
          icon={<Sparkles className="size-4" />}
          title="История с отметками"
          subtitle="Фото + отметить юзеров из пула"
          onClick={() => navigate("/app/stories")}
        />
      </div>
    </div>
  );
}

// ============================================================
// Bento widgets — overview block
// ============================================================

function BentoHero({
  value,
  delta,
  spark,
  onClick,
}: {
  value: number;
  delta: number;
  spark: number[];
  onClick?: () => void;
}) {
  const deltaPositive = delta > 0;
  const deltaZero = delta === 0;
  const peak = Math.max(0, ...spark);
  const peakIdx = spark.findIndex((v) => v === peak);

  return (
    <button
      onClick={() => {
        haptic.select();
        onClick?.();
      }}
      className={cn(
        "group relative w-full overflow-hidden rounded-2xl text-left",
        "border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01]",
        "hover:border-white/[0.1] transition-colors active:scale-[0.99]",
      )}
    >
      {/* Soft orange glow in corner */}
      <div
        className="absolute -top-16 -right-16 size-48 rounded-full bg-accent/15 blur-3xl pointer-events-none"
        aria-hidden
      />

      <div className="relative p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] text-text-dim uppercase tracking-[0.18em] font-medium">
              Отправлено за 7 дней
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[36px] leading-none font-semibold tabular-nums tracking-tight">
                {value.toLocaleString("ru")}
              </span>
              <span className="text-[11px] text-text-muted">публ.</span>
            </div>
          </div>
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded-md mt-1",
              deltaZero && "bg-white/[0.04] text-text-dim",
              !deltaZero && deltaPositive && "bg-success/15 text-success",
              !deltaZero && !deltaPositive && "bg-danger/15 text-danger",
            )}
          >
            {deltaPositive && <ArrowUpRight className="size-2.5" />}
            {deltaZero ? "—" : `${deltaPositive ? "+" : ""}${delta}%`}
          </span>
        </div>

        {/* Chart */}
        <div className="mt-3 -mx-1">
          <AreaChart
            data={spark.length ? spark : [0, 0, 0, 0, 0, 0, 0]}
            height={70}
            peakIdx={peakIdx >= 0 && peak > 0 ? peakIdx : null}
          />
        </div>
      </div>
    </button>
  );
}

function BentoRing({
  percent,
  label,
  sublabel,
  onClick,
}: {
  percent: number;
  label: string;
  sublabel: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick?.();
      }}
      className={cn(
        "relative w-full overflow-hidden rounded-2xl p-4 text-left",
        "border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.1]",
        "transition-colors active:scale-[0.98]",
      )}
    >
      <div className="flex items-center gap-3.5">
        <ProgressRing percent={percent} size={64} stroke={6} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-text-dim uppercase tracking-[0.18em] font-medium">
            {label}
          </div>
          <div className="text-[20px] leading-none font-semibold tabular-nums tracking-tight mt-1.5">
            {percent}
            <span className="text-[12px] text-text-muted">%</span>
          </div>
          <div className="text-[10px] text-text-dim truncate mt-1">{sublabel}</div>
        </div>
      </div>
    </button>
  );
}

function BentoStatList({
  rows,
}: {
  rows: {
    icon: React.ReactNode;
    label: string;
    value: number;
    max: number;
    onClick?: () => void;
  }[];
}) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-4 flex flex-col justify-between gap-3">
      {rows.map((r, i) => {
        const pct = r.max > 0 ? Math.min(100, Math.round((r.value / r.max) * 100)) : 0;
        return (
          <button
            key={i}
            onClick={() => {
              haptic.select();
              r.onClick?.();
            }}
            className="group w-full text-left active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] text-text-dim uppercase tracking-wider font-medium">
                <span className="size-4 rounded grid place-items-center bg-white/[0.04] text-text-muted">
                  {r.icon}
                </span>
                {r.label}
              </div>
              <div className="text-[12px] tabular-nums font-medium">
                <span className="text-text">{r.value}</span>
                <span className="text-text-dim"> / {r.max}</span>
              </div>
            </div>
            <div className="mt-1.5 h-1 rounded-full bg-white/[0.04] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 * i }}
                className="h-full rounded-full bg-gradient-to-r from-accent to-[#FF9A5C]"
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ProgressRing({
  percent,
  size = 64,
  stroke = 6,
}: {
  percent: number;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, percent)) / 100) * c;
  const gid = React.useId();

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF6B35" />
            <stop offset="100%" stopColor="#FFB07A" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          fill="none"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#${gid})`}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>
    </div>
  );
}

function AreaChart({
  data,
  height = 70,
  peakIdx = null,
}: {
  data: number[];
  height?: number;
  peakIdx?: number | null;
}) {
  const w = 320;
  const h = height;
  const pad = 6;
  const max = Math.max(1, ...data);
  const min = Math.min(0, ...data);
  const range = max - min || 1;
  const n = data.length;
  const step = n > 1 ? (w - pad * 2) / (n - 1) : 0;

  const pts = data.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return { x, y, v };
  });

  // Smooth catmull-rom-ish path
  const d =
    pts.length === 0
      ? ""
      : pts
          .map((p, i) => {
            if (i === 0) return `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
            const prev = pts[i - 1];
            const cx = (prev.x + p.x) / 2;
            return `C ${cx.toFixed(1)} ${prev.y.toFixed(1)}, ${cx.toFixed(1)} ${p.y.toFixed(1)}, ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
          })
          .join(" ");

  const areaD = pts.length ? `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${h} L ${pts[0].x.toFixed(1)} ${h} Z` : "";

  const gid = React.useId();
  const isFlat = max === min;
  const peak = peakIdx != null && peakIdx >= 0 && peakIdx < pts.length ? pts[peakIdx] : null;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      aria-hidden
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF6B35" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FF6B35" stopOpacity="0" />
        </linearGradient>
      </defs>
      {!isFlat && <path d={areaD} fill={`url(#${gid})`} />}
      <path
        d={d}
        fill="none"
        stroke="#FF6B35"
        strokeWidth={2}
        strokeOpacity={isFlat ? 0.4 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={isFlat ? "3 3" : undefined}
        vectorEffect="non-scaling-stroke"
        style={{ filter: isFlat ? undefined : "drop-shadow(0 0 4px rgba(255,107,53,0.55))" }}
      />
      {peak && (
        <g>
          <circle cx={peak.x} cy={peak.y} r={6} fill="#FF6B35" fillOpacity={0.15} />
          <circle cx={peak.x} cy={peak.y} r={3} fill="#FF6B35" />
          <circle cx={peak.x} cy={peak.y} r={1.4} fill="#fff" />
        </g>
      )}
    </svg>
  );
}

function ActionRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onClick?.();
      }}
      className="w-full glass rounded-2xl p-4 flex items-center gap-4 hover:bg-white/[0.05] transition-colors active:scale-[0.99]"
    >
      <div className="size-10 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-[15px] font-medium truncate">{title}</div>
        <div className="text-xs text-text-muted truncate mt-0.5">{subtitle}</div>
      </div>
      <ChevronRight className="size-4 text-text-dim shrink-0" />
    </button>
  );
}

function SubscriptionBanner({
  sub,
  onClick,
}: {
  sub: ReturnType<typeof useSubscription>["data"];
  onClick: () => void;
}) {
  const active = !!sub?.is_active;
  const expiring = active && (sub?.days_remaining ?? 0) <= 3;
  const days = sub?.days_remaining ?? 0;

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => {
        haptic.impact("medium");
        onClick();
      }}
      className={cn(
        "relative w-full overflow-hidden rounded-2xl text-left",
        "border border-accent/30 bg-gradient-to-br from-accent/[0.12] via-accent/[0.04] to-transparent",
        "active:scale-[0.99] transition-transform",
      )}
    >
      <div
        className="absolute -top-12 -right-12 size-32 rounded-full bg-accent/30 blur-3xl"
        aria-hidden
      />
      <div className="relative p-4 flex items-center gap-3">
        <div className="size-10 rounded-xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)] shrink-0">
          <Crown className="size-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold tracking-tight">
            {expiring
              ? `Подписка истекает через ${days} ${pluralDays(days)}`
              : active
              ? `${sub?.plan_title} активна`
              : "Активируйте подписку"}
          </div>
          <div className="text-[11px] text-text-muted mt-0.5">
            {active ? "Продлите доступ заранее" : "Тарифы от $7 — выберите подходящий"}
          </div>
        </div>
        <ChevronRight className="size-4 text-accent shrink-0" />
      </div>
    </motion.button>
  );
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "дня";
  return "дней";
}
